/**
 * Sales / SCRIPT module API client.
 * Base: /api/v1/sales/
 */
import apiClient from '@/api/client'

const base = '/sales'

export const salesApi = {
  // ============================================================
  // A1 — Activity Board
  // ============================================================
  ping(payload) {
    return apiClient.post(`${base}/activity/ping/`, payload)
  },
  writing(started, source) {
    return apiClient.post(`${base}/activity/writing/`, { started, source })
  },
  timeline(params) {
    return apiClient.get(`${base}/activity/timeline/`, { params })
  },
  teamSnapshot() {
    return apiClient.get(`${base}/activity/team/`)
  },

  // ============================================================
  // A3 — Team Dashboard
  // ============================================================
  teamDashboard() {
    return apiClient.get(`${base}/board/team-dashboard/`)
  },

  // Team Dashboard city filter — business markets only (region_cities / pipelines)
  boardCities() {
    return apiClient.get(`${base}/board/cities/`)
  },

  // ============================================================
  // A4 — Manager Profile
  // ============================================================
  managerProfile(userId) {
    return apiClient.get(`${base}/managers/${userId}/profile/`)
  },
  managerRecommendations(userId) {
    return apiClient.get(`${base}/managers/${userId}/recommendations/`)
  },
  managerProgress(userId) {
    return apiClient.get(`${base}/managers/${userId}/progress/`)
  },

  // ============================================================
  // A6 — Benchmarks
  // ============================================================
  benchmarkList(params) {
    return apiClient.get(`${base}/benchmarks/`, { params })
  },
  benchmarkPin(analysisId, pin = true, tags = '') {
    return apiClient.post(`${base}/benchmarks/pin/`, { analysis_id: analysisId, pin, tags })
  },
  benchmarkDetail(analysisId) {
    return apiClient.get(`${base}/benchmarks/${analysisId}/`)
  },

  // ============================================================
  // A5 — Coaching
  // ============================================================
  coachingPlans() {
    return apiClient.get(`${base}/coaching/plans/`)
  },
  coachingTasks(userId) {
    return apiClient.get(`${base}/coaching/tasks/${userId}/`)
  },
  coachingComplete(taskId, notes) {
    return apiClient.post(`${base}/coaching/tasks/${taskId}/complete/`, { notes })
  },

  // ============================================================
  // Settings — key/value + SCRIPT scenario editor
  // ============================================================
  getSettings() {
    return apiClient.get(`${base}/settings/`)
  },
  saveSettings(values) {
    return apiClient.put(`${base}/settings/`, { values })
  },
  getRICScenario() {
    return apiClient.get(`${base}/settings/ric-scenario/`)
  },
  saveRICScenario(payload) {
    return apiClient.put(`${base}/settings/ric-scenario/`, payload)
  },
  resetRICScenario() {
    return apiClient.post(`${base}/settings/ric-scenario/reset/`)
  },

  // ============================================================
  // Phase 6.5 — AI Prompts / IVR / Billing Costs (admin settings)
  // ============================================================
  aiPromptsList(params = {}) {
    return apiClient.get(`${base}/ai-prompts/`, { params })
  },
  aiPromptsLanguages() {
    return apiClient.get(`${base}/ai-prompts/languages/`)
  },
  aiPromptGet(id) {
    return apiClient.get(`${base}/ai-prompts/${id}/`)
  },
  aiPromptUpdate(id, payload) {
    return apiClient.patch(`${base}/ai-prompts/${id}/`, payload)
  },
  ivrMenusList() {
    return apiClient.get(`${base}/ivr-menus/`)
  },
  billingCostsList(params = {}) {
    return apiClient.get(`${base}/billing-costs/`, { params })
  },
  billingCostUpdate(id, payload) {
    return apiClient.patch(`${base}/billing-costs/${id}/`, payload)
  },

  // ============================================================
  // A7 — Deal Probability feed
  // ============================================================
  dealProbability(params) {
    return apiClient.get(`${base}/deal-probability/`, { params })
  },

  // ============================================================
  // v2 — Deals (HubSpot-style) over crm_leads
  // ============================================================
  // Repeated keys (stage_id=1&stage_id=2): the API reads lists with getlist().
  dealsList(params) { return apiClient.get(`${base}/deals/`, { params, paramsSerializer: { indexes: null } }) },
  dealsDict() { return apiClient.get(`${base}/deals/dictionaries/`) },
  dealsCreate(payload) { return apiClient.post(`${base}/deals/`, payload) },
  dealGet(id) { return apiClient.get(`${base}/deals/${id}/`) },
  dealUpdate(id, payload) { return apiClient.patch(`${base}/deals/${id}/`, payload) },
  dealDelete(id) { return apiClient.delete(`${base}/deals/${id}/`) },
  dealsBulk(payload) { return apiClient.post(`${base}/deals/bulk/`, payload) },
  dealCardFieldsSave(payload) { return apiClient.patch(`${base}/deals/card-fields/`, payload) },
  dealLinkedStage(params) { return apiClient.get(`${base}/deals/linked-stage/`, { params }) },

  // === Deal owner re-assignment (LSA + general use) ===
  dealsSellers() { return apiClient.get(`${base}/deals/sellers/`) },
  dealClaim(id) { return apiClient.post(`${base}/deals/${id}/claim/`) },
  dealAssign(id, targetUserId) {
    return apiClient.post(`${base}/deals/${id}/assign/`, { target_user_id: targetUserId })
  },

  // === LSA messages (per-deal feed + outbound reply) ===
  lsaMessages(dealId) {
    return apiClient.get('/integrations/lsa/messages', { params: { deal_id: dealId } })
  },
  lsaReply(dealId, text) {
    return apiClient.post('/integrations/lsa/reply', { deal_id: dealId, text })
  },

  // === LSA admin (settings + accounts + webhook logs) ===
  lsaSettings() { return apiClient.get('/integrations/lsa/settings') },
  // One-click "Connect Google" — returns { ok, auth_url } to redirect the browser.
  lsaOauthStart() { return apiClient.get('/integrations/lsa/oauth/start') },
  lsaSettingsUpsert(payload) { return apiClient.post('/integrations/lsa/settings/upsert', payload) },
  lsaSettingsTest() { return apiClient.post('/integrations/lsa/settings/test') },
  lsaSettingsSyncNow(payload) { return apiClient.post('/integrations/lsa/settings/sync_now', payload || {}) },
  lsaSettingsStartWatch() { return apiClient.post('/integrations/lsa/settings/start_watch') },
  lsaSettingsStopWatch() { return apiClient.post('/integrations/lsa/settings/stop_watch') },
  lsaOauthStart() { return apiClient.get('/integrations/lsa/oauth/start') },
  lsaOauthDisconnect() { return apiClient.post('/integrations/lsa/oauth/disconnect', {}) },
  lsaAccounts() { return apiClient.get('/integrations/lsa/accounts') },
  lsaAccountsDiscover() { return apiClient.get('/integrations/lsa/accounts/discover') },
  lsaAccountsApply(customerIds) {
    return apiClient.post('/integrations/lsa/accounts/apply', { customer_ids: customerIds })
  },
  lsaAccountUpsert(payload) { return apiClient.post('/integrations/lsa/accounts/upsert', payload) },
  lsaAccountDeactivate(id) { return apiClient.delete(`/integrations/lsa/accounts/${id}/deactivate`) },
  lsaWebhookLogs(limit = 50) {
    return apiClient.get('/integrations/lsa/webhook_logs', { params: { limit } })
  },
  dealsExport(params) {
    return apiClient.get(`${base}/deals/export/`, { params, responseType: 'blob' })
  },
  dealStageCreate(payload) { return apiClient.post(`${base}/deals/stages/`, payload) },
  dealStageUpdate(id, payload) { return apiClient.patch(`${base}/deals/stages/${id}/`, payload) },
  dealStagesList(params = {}) { return apiClient.get(`${base}/deals/stages/`, { params }) },
  dealStageDelete(id, moveTo = null) {
    return apiClient.delete(`${base}/deals/stages/${id}/`, { params: moveTo ? { move_to: moveTo } : {} })
  },
  dealViewsList() { return apiClient.get(`${base}/deals/views/`) },
  dealViewsCreate(payload) { return apiClient.post(`${base}/deals/views/`, payload) },
  dealViewsUpdate(id, payload) { return apiClient.patch(`${base}/deals/views/${id}/`, payload) },
  dealViewsDelete(id) { return apiClient.delete(`${base}/deals/views/${id}/`) },

  // ============================================================
  // Commercial Site Assessment (Opportunity) — cleaning-audit §4.10
  // ============================================================
  assessmentsList(params) { return apiClient.get(`${base}/assessments/`, { params }) },
  assessmentsDict() { return apiClient.get(`${base}/assessments/dictionaries/`) },
  assessmentsConversion(params) {
    return apiClient.get(`${base}/assessments/conversion-rate/`, { params })
  },
  assessmentCreate(payload) { return apiClient.post(`${base}/assessments/`, payload) },
  assessmentGet(id) { return apiClient.get(`${base}/assessments/${id}/`) },
  assessmentUpdate(id, payload) { return apiClient.patch(`${base}/assessments/${id}/`, payload) },
  assessmentDelete(id) { return apiClient.delete(`${base}/assessments/${id}/`) },
  assessmentSaveZones(id, zones) {
    return apiClient.put(`${base}/assessments/${id}/zones/`, { zones })
  },
  assessmentProposals(id) { return apiClient.get(`${base}/assessments/${id}/proposals/`) },
  assessmentGenerateProposal(id) {
    return apiClient.post(`${base}/assessments/${id}/proposals/`)
  },
  assessmentTransition(id, payload) {
    return apiClient.post(`${base}/assessments/${id}/transition/`, payload)
  },

  // ============================================================
  // v2 — Day Tape drill-down
  // ============================================================
  dayTapeDrill(params) {
    return apiClient.get(`${base}/activity/day-tape/drill/`, { params })
  },

  // ============================================================
  // v2 — Speed-to-Lead
  // ============================================================
  stlSummary(params) { return apiClient.get(`${base}/speed-to-lead/summary/`, { params }) },
  stlRecent(params) { return apiClient.get(`${base}/speed-to-lead/recent/`, { params }) },

  // ============================================================
  // v2 — Alerts
  // ============================================================
  alertRules() { return apiClient.get(`${base}/alerts/rules/`) },
  alertRuleUpdate(id, payload) {
    return apiClient.patch(`${base}/alerts/rules/${id}/`, payload)
  },
  alertFeed(params) { return apiClient.get(`${base}/alerts/feed/`, { params }) },

  // ============================================================
  // v2 — Supervisor actions
  // ============================================================
  superMessage(payload) { return apiClient.post(`${base}/supervisor/message/`, payload) },
  superCoach(payload) { return apiClient.post(`${base}/supervisor/coach/`, payload) },
  superHistory(params) { return apiClient.get(`${base}/supervisor/history/`, { params }) },

  // ============================================================
  // v2 — Simulator
  // ============================================================
  simExercises(params) { return apiClient.get(`${base}/simulator/exercises/`, { params }) },
  simExercise(id) { return apiClient.get(`${base}/simulator/exercises/${id}/`) },
  simCreateExercise(payload) {
    return apiClient.post(`${base}/simulator/exercises/`, payload)
  },
  simUpdateExercise(id, payload) {
    return apiClient.patch(`${base}/simulator/exercises/${id}/`, payload)
  },
  simDeleteExercise(id) {
    return apiClient.delete(`${base}/simulator/exercises/${id}/`)
  },
  simAttempts(params) { return apiClient.get(`${base}/simulator/attempts/`, { params }) },
  simStart(exerciseId) {
    return apiClient.post(`${base}/simulator/attempts/`, { exercise_id: exerciseId })
  },
  simMessage(attemptId, content) {
    return apiClient.post(`${base}/simulator/attempts/${attemptId}/message/`, { content })
  },
  simFinish(attemptId) {
    return apiClient.post(`${base}/simulator/attempts/${attemptId}/finish/`)
  },
  simTranscribe(attemptId, audioBlob) {
    const fd = new FormData()
    fd.append('audio', audioBlob, 'speech.webm')
    return apiClient.post(`${base}/simulator/attempts/${attemptId}/transcribe/`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  simTTS(text, voiceId = '') {
    const body = { text }
    if (voiceId) body.voice_id = voiceId
    return apiClient.post(`${base}/simulator/tts/`, body, {
      responseType: 'blob',
    })
  },
  simVoices() {
    return apiClient.get(`${base}/simulator/voices/`)
  },
  simTeamAnalytics(params) {
    return apiClient.get(`${base}/simulator/analytics/team/`, { params })
  },

  // ============================================================
  // v2 — Team Dashboard
  // ============================================================
  dashboardV2(params) { return apiClient.get(`${base}/board/dashboard-v2/`, { params }) },
  dashboardQuartiles(params) { return apiClient.get(`${base}/board/dashboard-v2/quartiles/`, { params }) },
  dashboardReasonOfLoss(params) {
    return apiClient.get(`${base}/board/dashboard-v2/reason-of-loss/`, { params })
  },
  dashboardSparkline(params) {
    return apiClient.get(`${base}/board/dashboard-v2/sparkline/`, { params })
  },
  // PDF §2.9: per-manager sparkline (7d call counts for leaderboard rows)
  dashboardSparklinePerUser(params) {
    return apiClient.get(`${base}/board/dashboard-v2/sparkline-per-user/`, { params })
  },
  // PDF §2.16: RIC by call_type 30/90 days
  dashboardRicByType(params) {
    return apiClient.get(`${base}/board/dashboard-v2/ric-by-type/`, { params })
  },
  dashboardExport(params) {
    return apiClient.get(`${base}/board/dashboard-v2/export/`, { params, responseType: 'blob' })
  },
  goalsList(params) { return apiClient.get(`${base}/board/goals/`, { params }) },
  goalsUpsert(payload) { return apiClient.put(`${base}/board/goals/`, payload) },
  goalsDelete(id) { return apiClient.delete(`${base}/board/goals/`, { params: { id } }) },
  dashboardTopMistakes(params) {
    return apiClient.get(`${base}/board/dashboard-v2/top-mistakes/`, { params })
  },
  dashboardCompare(params) {
    return apiClient.get(`${base}/board/dashboard-v2/compare/`, { params })
  },

  // ============================================================
  // v2 — Manager Profile extensions
  // ============================================================
  managerOverviewV2(id, params) {
    return apiClient.get(`${base}/managers/${id}/overview-v2/`, { params })
  },
  managerFunnelInOut(id, params) {
    return apiClient.get(`${base}/managers/${id}/funnel-inout/`, { params })
  },
  managerGroupedIssues(id, params) {
    return apiClient.get(`${base}/managers/${id}/grouped-issues/`, { params })
  },
  managerFunnel(id, params) {
    return apiClient.get(`${base}/managers/${id}/funnel/`, { params })
  },
  managerStrengths(id, params) {
    return apiClient.get(`${base}/managers/${id}/strengths/`, { params })
  },
  managerBenchmarks(id) {
    return apiClient.get(`${base}/managers/${id}/benchmarks/`)
  },
  managerDirectionSplit(id, params) {
    return apiClient.get(`${base}/managers/${id}/direction-split/`, { params })
  },
  managerIssueDynamics(id) {
    return apiClient.get(`${base}/managers/${id}/issue-dynamics/`)
  },

  // ============================================================
  // v2 — Benchmarks Library (category/city/star/tag filters + AI explain)
  // ============================================================
  benchmarksV2(params) { return apiClient.get(`${base}/benchmarks-v2/`, { params }) },
  benchmarksV2Cats() { return apiClient.get(`${base}/benchmarks-v2/categories/`) },
  benchmarksV2Tag(analysisId, payload) {
    return apiClient.post(`${base}/benchmarks-v2/${analysisId}/tag/`, payload)
  },
  benchmarksV2Explain(analysisId) {
    return apiClient.post(`${base}/benchmarks-v2/${analysisId}/explain/`)
  },
  benchmarksV2Timeline(analysisId) {
    return apiClient.get(`${base}/benchmarks-v2/${analysisId}/timeline/`)
  },

  // ============================================================
  // v2 — Live AI Coach
  // ============================================================
  coachLive(payload) { return apiClient.post(`${base}/coach/live/`, payload) },

  // ============================================================
  // v5 — Manager Profile: Issues/Strengths/Tips + benchmark UX
  // ============================================================
  managerIssuesV2(id, params) {
    return apiClient.get(`${base}/managers/${id}/issues-v2/`, { params })
  },
  managerStrengthsV2(id, params) {
    return apiClient.get(`${base}/managers/${id}/strengths-v2/`, { params })
  },
  managerTipsV2(id, params) {
    return apiClient.get(`${base}/managers/${id}/tips-v2/`, { params })
  },
  managerBenchmarksV2(id, params) {
    return apiClient.get(`${base}/managers/${id}/benchmarks-v2/`, { params })
  },
  tipProgress(payload) {
    return apiClient.post(`${base}/tips-progress/`, payload)
  },
  tipFeedback(payload) {
    return apiClient.post(`${base}/tips-feedback/`, payload)
  },
  benchmarkListen(analysisId, payload = {}) {
    return apiClient.post(`${base}/benchmarks/${analysisId}/listen/`, payload)
  },
  benchmarkComments(analysisId) {
    return apiClient.get(`${base}/benchmarks/${analysisId}/comments/`)
  },
  benchmarkAddComment(analysisId, payload) {
    return apiClient.post(`${base}/benchmarks/${analysisId}/comments/`, payload)
  },

  // ============================================================
  // v6 — Coaching / Dynamics / Progress
  // ============================================================
  coachingV2Tasks(params) {
    return apiClient.get(`${base}/coaching-v2/tasks/`, { params })
  },
  coachingV2CreateTask(payload) {
    return apiClient.post(`${base}/coaching-v2/tasks/`, payload)
  },
  coachingV2UpdateTask(taskId, payload) {
    return apiClient.patch(`${base}/coaching-v2/tasks/${taskId}/`, payload)
  },
  coachingV2Templates() {
    return apiClient.get(`${base}/coaching-v2/templates/`)
  },
  coachingV2FromTemplate(payload) {
    return apiClient.post(`${base}/coaching-v2/from-template/`, payload)
  },
  coachingV2FromIssue(payload) {
    return apiClient.post(`${base}/coaching-v2/from-issue/`, payload)
  },
  managerDynamicsV2(id, params) {
    return apiClient.get(`${base}/managers/${id}/dynamics-v2/`, { params })
  },
  managerProgressHeatmap(id, params) {
    return apiClient.get(`${base}/managers/${id}/progress-heatmap/`, { params })
  },

  // ============================================================
  // v7 — AI Coach v2 + Daily Insights
  // ============================================================
  aiCoachRules(params) {
    return apiClient.get(`${base}/ai-coach/rules/`, { params })
  },
  aiCoachCreateRule(payload) {
    return apiClient.post(`${base}/ai-coach/rules/`, payload)
  },
  aiCoachUpdateRule(ruleId, payload) {
    return apiClient.patch(`${base}/ai-coach/rules/${ruleId}/`, payload)
  },
  aiCoachDeleteRule(ruleId) {
    return apiClient.delete(`${base}/ai-coach/rules/${ruleId}/`)
  },
  aiCoachRunNow() {
    return apiClient.post(`${base}/ai-coach/run-now/`)
  },
  managerDailyInsights(userId, params) {
    return apiClient.get(`${base}/managers/${userId}/daily-insights/`, { params })
  },
  dailyInsightUpdate(insightId, payload) {
    return apiClient.patch(`${base}/daily-insights/${insightId}/`, payload)
  },
  dailyInsightsGenerateNow() {
    return apiClient.post(`${base}/daily-insights/generate-now/`)
  },

  // ============================================================
  // v8 — Polish: PDF export + badges
  // ============================================================
  managerReportPdf(userId, params) {
    return apiClient.get(`${base}/managers/${userId}/report-pdf/`, {
      params,
      responseType: 'blob',
    })
  },
  managerBadges(userId) {
    return apiClient.get(`${base}/managers/${userId}/badges/`)
  },

  // ============================================================
  // Activity Board — color palette (admin/owner editable)
  // ============================================================
  activityColors() {
    return apiClient.get(`${base}/settings/activity-colors/`)
  },
  saveActivityColors(palette) {
    return apiClient.put(`${base}/settings/activity-colors/`, { palette })
  },
  resetActivityColors() {
    return apiClient.post(`${base}/settings/activity-colors/reset/`)
  },

  // ============================================================
  // Activity Board — per-user hour-range preference
  // Saved in user_settings.settings_json.sales_activity_board so each
  // operator has their own window (teams span many timezones).
  // ============================================================
  activityBoardPrefs() {
    return apiClient.get(`${base}/settings/activity-board-prefs/`)
  },
  saveActivityBoardPrefs({ start_hour, end_hour }) {
    return apiClient.put(`${base}/settings/activity-board-prefs/`, {
      start_hour, end_hour,
    })
  },

  // ============================================================
  // Sales rework Phase-2 — additional Manager Profile endpoints
  // ============================================================
  managerRank(userId, params) {
    return apiClient.get(`${base}/managers/${userId}/rank/`, { params })
  },
  managerStreak(userId) {
    return apiClient.get(`${base}/managers/${userId}/streak/`)
  },
  managerUnclosed(userId, params) {
    return apiClient.get(`${base}/managers/${userId}/unclosed-deals/`, { params })
  },
  managerSkillsRadar(userId, params) {
    return apiClient.get(`${base}/managers/${userId}/skills-radar/`, { params })
  },
  managerFunnelHistory(userId, params) {
    return apiClient.get(`${base}/managers/${userId}/funnel-history/`, { params })
  },
  managerImprovementPlansList(userId, params) {
    return apiClient.get(`${base}/managers/${userId}/improvement-plans/`, { params })
  },
  managerImprovementPlanCreate(userId, payload) {
    return apiClient.post(`${base}/managers/${userId}/improvement-plans/`, payload)
  },
  managerImprovementPlanUpdate(planId, payload) {
    return apiClient.patch(`${base}/improvement-plans/${planId}/`, payload)
  },
  managerImprovementPlanDelete(planId) {
    return apiClient.delete(`${base}/improvement-plans/${planId}/`)
  },
  managerGoalsGet(userId) {
    return apiClient.get(`${base}/managers/${userId}/goals/`)
  },
  managerGoalsSave(userId, payload) {
    return apiClient.put(`${base}/managers/${userId}/goals/`, payload)
  },
  managerFollowUp(params) {
    return apiClient.get(`${base}/follow-up/`, { params })
  },
  managerFollowUpDone(taskId, payload) {
    return apiClient.post(`${base}/follow-up/${taskId}/done/`, payload || {})
  },
  managerStudyAttempts(userId, params) {
    return apiClient.get(`${base}/managers/${userId}/study/`, { params })
  },
  managerBenchmarkAssignments(params) {
    return apiClient.get(`${base}/benchmark-assignments/`, { params })
  },
  benchmarkAssignmentListened(assignmentId, payload) {
    return apiClient.post(
      `${base}/benchmark-assignments/${assignmentId}/listened/`, payload || {})
  },
  lostReasonsDict() {
    return apiClient.get(`${base}/lost-reasons/`)
  },
  citiesDict() {
    return apiClient.get(`${base}/cities/`)
  },
  userPrefsGet(scope) {
    return apiClient.get(`${base}/preferences/${scope}/`)
  },
  userPrefsSave(scope, key, payload) {
    return apiClient.put(`${base}/preferences/${scope}/${key}/`, payload)
  },
  aiCoachPending(params) {
    return apiClient.get(`${base}/ai-coach/pending/`, { params })
  },
  aiCoachMy(params) {
    return apiClient.get(`${base}/ai-coach/my/`, { params })
  },
  aiCoachApprove(assignmentId) {
    return apiClient.post(`${base}/ai-coach/${assignmentId}/approve/`)
  },
  aiCoachDisapprove(assignmentId, reason) {
    return apiClient.post(`${base}/ai-coach/${assignmentId}/disapprove/`, { reason })
  },
  aiCoachManualAssign(payload) {
    return apiClient.post(`${base}/ai-coach/manual/`, payload)
  },
  aiCoachComplete(assignmentId, payload) {
    return apiClient.post(`${base}/ai-coach/${assignmentId}/complete/`, payload || {})
  },
  insightDismiss(insightId, payload) {
    return apiClient.post(`${base}/daily-insights/${insightId}/dismiss/`, payload || {})
  },
  studyStart(articleId, payload) {
    return apiClient.post(`${base}/study/${articleId}/start/`, payload || {})
  },
  studyComplete(attemptId, payload) {
    return apiClient.post(`${base}/study/${attemptId}/complete/`, payload || {})
  },

  // ============================================================
  // Sales rework — My Homework (раздел N)
  // ============================================================
  myHomework(params) {
    return apiClient.get(`${base}/my-homework/`, { params })
  },
  myHomeworkComplete(assignmentId, payload) {
    return apiClient.post(`${base}/my-homework/${assignmentId}/complete/`, payload || {})
  },
}

export default salesApi
